import type { ItemId } from '@finsim/model';
import { useUnit } from 'effector-react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ITEM_GROUPS } from '../../../entities/project';
import { itemSelected } from '../../../entities/selection';
import { cn } from '../../../shared/lib/cn';
import { Dialog, DialogContent, IconButton, Tooltip } from '../../../shared/ui';
import { explainMonth } from '../lib/series';
import { $explain, explainClosed, explainOpened } from '../model';
import type { CanvasData } from './use-canvas';

/** "What happened this month": every cash movement of a variant in one month (nominal). */
export function ExplainDialog({ data }: { data: CanvasData }) {
  const { t } = useTranslation();
  const [target, close, open, select] = useUnit([
    $explain,
    explainClosed,
    explainOpened,
    itemSelected,
  ]);
  const { project, result, dates, fmt } = data;
  const scenario = target ? project.scenarios.find((s) => s.id === target.scenarioId) : undefined;
  const scenarioResult = target ? result.scenarios[target.scenarioId] : undefined;
  const date = target ? dates[target.index] : undefined;
  const e =
    target && scenario && scenarioResult
      ? explainMonth(project, scenario.id, scenarioResult, target.index)
      : null;

  const step = (delta: number): void => {
    if (!target) return;
    const index = Math.min(result.months - 1, Math.max(0, target.index + delta));
    open({ scenarioId: target.scenarioId, index });
  };

  const money = (v: number, signed = false): string => (signed ? fmt.signed(v) : fmt.money(v));

  return (
    <Dialog
      open={e !== null}
      onOpenChange={(o) => {
        if (!o) close();
      }}
    >
      {e && target && scenario && (
        <DialogContent
          title={t('explain.title', { date: date ? fmt.monthLong(date) : '' })}
          description={t('explain.description', { name: scenario.name })}
          closeLabel={t('common.close')}
          className="w-[min(520px,calc(100vw-32px))]"
        >
          <div
            onKeyDown={(ev) => {
              if (ev.key === 'ArrowLeft') step(-1);
              if (ev.key === 'ArrowRight') step(1);
            }}
          >
            <div className="mb-3 flex items-center justify-between">
              <Tooltip content={t('explain.previous')} shortcut={['←']}>
                <IconButton
                  label={t('explain.previous')}
                  disabled={target.index === 0}
                  onClick={() => {
                    step(-1);
                  }}
                >
                  <ChevronLeft />
                </IconButton>
              </Tooltip>
              <span className="text-xs text-text-3">{t('explain.nominalNote')}</span>
              <Tooltip content={t('explain.next')} shortcut={['→']}>
                <IconButton
                  label={t('explain.next')}
                  disabled={target.index >= result.months - 1}
                  onClick={() => {
                    step(1);
                  }}
                >
                  <ChevronRight />
                </IconButton>
              </Tooltip>
            </div>
            <table className="w-full text-sm">
              <caption className="sr-only">
                {t('explain.title', { date: date ? fmt.monthLong(date) : '' })}
              </caption>
              <tbody>
                <tr className="border-b border-border-subtle">
                  <th scope="row" className="py-1.5 text-left font-normal text-text-3">
                    {t('explain.opening')}
                  </th>
                  <td className="tabular py-1.5 text-right">{money(e.opening)}</td>
                  <td className="w-8" />
                </tr>
                {e.lines.length === 0 && (
                  <tr>
                    <td colSpan={3} className="py-3 text-center text-text-3">
                      {t('explain.noMovements')}
                    </td>
                  </tr>
                )}
                {ITEM_GROUPS.flatMap((group) => {
                  const lines = e.lines.filter((l) => l.group === group);
                  if (lines.length === 0) return [];
                  return [
                    <tr key={`g-${group}`}>
                      <th
                        colSpan={3}
                        scope="colgroup"
                        className="pt-2.5 pb-0.5 text-left text-2xs font-medium text-text-3"
                      >
                        {t(`groups.${group}`)}
                      </th>
                    </tr>,
                    ...lines.map((line) => (
                      <tr key={line.itemId} className="group hover:bg-bg-3">
                        <th scope="row" className="py-1 pl-2 text-left font-normal text-text-2">
                          {line.name}
                        </th>
                        <td
                          className={cn(
                            'tabular py-1 text-right',
                            line.amount < 0 && 'text-negative',
                          )}
                        >
                          {money(line.amount, true)}
                        </td>
                        <td className="text-right">
                          <IconButton
                            size="sm"
                            label={t('explain.goToItem', { name: line.name })}
                            className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
                            onClick={() => {
                              select({ itemId: line.itemId as ItemId, scenarioId: scenario.id });
                              close();
                            }}
                          >
                            <ArrowRight />
                          </IconButton>
                        </td>
                      </tr>
                    )),
                  ];
                })}
                {e.overdraftInterest !== 0 && (
                  <tr>
                    <th scope="row" className="py-1 pl-2 text-left font-normal text-text-2">
                      {t('ledger.overdraft')}
                    </th>
                    <td className="tabular py-1 text-right text-negative">
                      {money(-e.overdraftInterest, true)}
                    </td>
                    <td />
                  </tr>
                )}
                <tr className="border-t border-border-subtle font-medium">
                  <th scope="row" className="pt-2 text-left">
                    {t('explain.closing')}
                  </th>
                  <td className={cn('tabular pt-2 text-right', e.closing < 0 && 'text-negative')}>
                    {money(e.closing)}
                  </td>
                  <td />
                </tr>
              </tbody>
            </table>
            <dl className="mt-4 grid grid-cols-3 gap-2 rounded-md bg-bg-1 p-3 text-xs">
              {(
                [
                  ['assets', e.assets],
                  ['liabilities', e.liabilities],
                  ['netWorth', e.netWorth],
                ] as const
              ).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-text-3">{t(`explain.${key}`)}</dt>
                  <dd className="tabular mt-0.5 font-medium">{money(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
